import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function ListService() {
  useSeo("List Your Service — FREE | OyeBest", "List your local service for free on OyeBest and start getting customers from your city.");
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    name: "", owner_name: "", category_slug: "", city_slug: "etah", address: "", service_area: "",
    phone: "", whatsapp: "", maps_url: "", description: "", pricing: "", services: "",
  });

  useEffect(() => {
    api.get("/service-categories").then((r) => setCategories(r.data));
    api.get("/cities").then((r) => setCities(r.data));
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.category_slug || !f.phone) { toast.error("Provider name, service category and phone are required"); return; }
    setLoading(true);
    try {
      await api.post("/service-registration", {
        ...f,
        listing_type: "service",
        services: f.services ? f.services.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) { toast.error("Could not submit. Please try again."); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="w-8 h-8" /></div>
        <h1 className="font-display text-3xl font-bold">Aapki service submit ho gayi!</h1>
        <p className="text-muted-foreground mt-3">Your service listing is now <b>Pending</b> admin approval. Once approved, it will be published and visible to everyone.</p>
        <Button variant="outline" className="mt-6 rounded-full" onClick={() => navigate("/services")}>Back to Services</Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-11 h-11 rounded-xl bg-accent text-primary flex items-center justify-center"><Wrench className="w-5 h-5" /></span>
        <span className="rounded-full bg-green-100 text-green-700 px-3 py-1 text-xs font-bold">100% FREE</span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">List Your Service — FREE</h1>
      <p className="text-muted-foreground mt-2">Apni service list karein aur apne shehar ke customers tak pahunchein.</p>

      <form onSubmit={submit} className="mt-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Provider / Business Name *"><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl h-12" data-testid="ls-name" /></Field>
          <Field label="Your Name"><Input value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)} className="rounded-xl h-12" data-testid="ls-owner" /></Field>
          <Field label="Service Category *">
            <Select value={f.category_slug} onValueChange={(v) => set("category_slug", v)}>
              <SelectTrigger className="rounded-xl h-12" data-testid="ls-category"><SelectValue placeholder="Select service" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="City *">
            <Select value={f.city_slug} onValueChange={(v) => set("city_slug", v)}>
              <SelectTrigger className="rounded-xl h-12" data-testid="ls-city"><SelectValue placeholder="Select city" /></SelectTrigger>
              <SelectContent>{cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Phone *"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-xl h-12" data-testid="ls-phone" /></Field>
          <Field label="WhatsApp"><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="rounded-xl h-12" data-testid="ls-whatsapp" /></Field>
        </div>
        <Field label="Address / Shop Location"><Input value={f.address} onChange={(e) => set("address", e.target.value)} className="rounded-xl h-12" data-testid="ls-address" /></Field>
        <Field label="Service Area (areas you cover)"><Input value={f.service_area} onChange={(e) => set("service_area", e.target.value)} className="rounded-xl h-12" placeholder="e.g. Etah and nearby areas" data-testid="ls-service-area" /></Field>
        <Field label="Pricing (optional)"><Input value={f.pricing} onChange={(e) => set("pricing", e.target.value)} className="rounded-xl h-12" placeholder="e.g. From ₹199 / visit" data-testid="ls-pricing" /></Field>
        <Field label="Google Maps Location (URL)"><Input value={f.maps_url} onChange={(e) => set("maps_url", e.target.value)} className="rounded-xl h-12" placeholder="https://maps.google.com/…" data-testid="ls-maps" /></Field>
        <Field label="Description"><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl" rows={3} data-testid="ls-desc" /></Field>
        <Field label="What you offer (comma separated)"><Input value={f.services} onChange={(e) => set("services", e.target.value)} className="rounded-xl h-12" placeholder="Installation, Repair, Servicing" data-testid="ls-services" /></Field>

        <Button type="submit" disabled={loading} className="w-full rounded-full h-13 py-3 text-base font-semibold" data-testid="ls-submit">
          {loading ? "Submitting…" : "Submit Service — FREE"}
        </Button>
        <p className="text-xs text-muted-foreground text-center">Your listing will be reviewed by our team before publishing. You can also <button type="button" className="text-primary font-semibold" onClick={() => navigate("/owner")}>login as a provider</button> to manage it, get enquiries and go Premium.</p>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-sm font-semibold">{label}</Label>{children}</div>;
}
